import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  getMonthWiseStats,
  getDayWiseStats,
} from "../../services/analyticsService";
import { formatAmount } from "../../utils/formatters";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const toLocalMidnight = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

const getInitialStartDate = (timeRange, endDate = new Date()) => {
  if (timeRange === "month") {
    return toLocalMidnight(
      new Date(endDate.getFullYear(), endDate.getMonth() - 2, 1)
    ); // 3 months (current month + 2 previous months)
  } else if (timeRange === "hour") {
    // For hourly, default to last 24 hours
    const yesterday = new Date(endDate);
    yesterday.setHours(yesterday.getHours() - 24);
    return yesterday;
  } else {
    const d = new Date(endDate);
    d.setDate(d.getDate() - 90); // 3 months (90 days)
    return toLocalMidnight(d);
  }
};

const ZoneDetailSalesTrend = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const zoneParam = location.state?.zone || "";

  // Initialize timeRange first
  const initialTimeRange = location.state?.timeRange || "month";
  
  const [timeRange, setTimeRange] = useState(initialTimeRange);
  const [endDate, setEndDate] = useState(
    location.state?.endDate
      ? (initialTimeRange === "hour" ? new Date(location.state.endDate) : toLocalMidnight(new Date(location.state.endDate)))
      : (initialTimeRange === "hour" ? new Date() : toLocalMidnight(new Date()))
  );
  const [startDate, setStartDate] = useState(
    location.state?.startDate
      ? (initialTimeRange === "hour" ? new Date(location.state.startDate) : toLocalMidnight(new Date(location.state.startDate)))
      : (() => {
          const initialDate = getInitialStartDate(
            initialTimeRange,
            location.state?.endDate
              ? new Date(location.state.endDate)
              : new Date()
          );
          return initialTimeRange === "hour" ? initialDate : toLocalMidnight(initialDate);
        })()
  );
  const [postingAccount, setPostingAccount] = useState(
    location.state?.postingAccount || ""
  );
  const [showQuantity, setShowQuantity] = useState(
    location.state?.showQuantity || false
  );
  const [showAllStates, setShowAllStates] = useState(
    location.state?.showAllStates || false
  );
  const [salesData, setSalesData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Track previous timeRange to detect when switching
  const prevTimeRangeRef = useRef(timeRange);
  
  // Adjust dates when switching time ranges
  useEffect(() => {
    // Only adjust when switching TO a different time range
    if (prevTimeRangeRef.current === timeRange) {
      prevTimeRangeRef.current = timeRange;
      // If endDate changes and startDate is not from location.state, update startDate
      if (!location.state?.startDate) {
        const newStartDate = getInitialStartDate(timeRange, endDate);
        setStartDate(timeRange === "hour" ? newStartDate : toLocalMidnight(newStartDate));
      }
      return;
    }

    if (timeRange === "month" && endDate) {
      const endDateMonth = endDate.getMonth();
      const endDateYear = endDate.getFullYear();
      
      // Always set start date to 1st of the month when in monthly view
      const firstDayOfMonth = new Date(endDateYear, endDateMonth, 1);
      setStartDate(toLocalMidnight(firstDayOfMonth));
      
      // Set end date to last day of the month
      const lastDayOfMonth = new Date(endDateYear, endDateMonth + 1, 0);
      setEndDate(toLocalMidnight(lastDayOfMonth));
    } else if (timeRange === "hour" && endDate) {
      // For hourly, limit to last 24 hours by default
      const newStartDate = new Date(endDate);
      newStartDate.setHours(newStartDate.getHours() - 24);
      setStartDate(newStartDate);
    } else if (!location.state?.startDate) {
      const newStartDate = getInitialStartDate(timeRange, endDate);
      setStartDate(toLocalMidnight(newStartDate));
    }
    
    // Update the ref for next comparison
    prevTimeRangeRef.current = timeRange;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeRange, endDate]);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        // Format dates - backend stats endpoints expect YYYY-MM-DD format for all time ranges
        // Helper function to format date as YYYY-MM-DD
        const formatDateToYYYYMMDD = (date) => {
          if (!date) return null;
          let dateObj = date;
          if (!(date instanceof Date)) {
            dateObj = new Date(date);
          }
          if (isNaN(dateObj.getTime())) return null;
          const year = dateObj.getFullYear();
          const month = String(dateObj.getMonth() + 1).padStart(2, '0');
          const day = String(dateObj.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        };

        const formattedStartDate = formatDateToYYYYMMDD(startDate);
        const formattedEndDate = formatDateToYYYYMMDD(endDate);

        if (!formattedStartDate || !formattedEndDate) {
          throw new Error("Invalid date format");
        }

        const params = {
          startDate: formattedStartDate,
          endDate: formattedEndDate,
          statType: "state",
          zone: zoneParam,
          // Note: Backend doesn't support hourly grouping for stats endpoints yet
          ...(postingAccount && { postingAccount }),
        };
        const data =
          timeRange === "month"
            ? await getMonthWiseStats(params)
            : await getDayWiseStats(params);
        setSalesData(data);
      } catch (err) {
        setError(err.message || "Failed to load data");
      } finally {
        setIsLoading(false);
      }
    };
    if (zoneParam) fetchData();
  }, [timeRange, startDate, endDate, postingAccount, zoneParam]);


  const formatCell = (data) => {
    if (!data) return showQuantity ? "0" : "₹0";
    return showQuantity
      ? `${data.qty}`
      : `${formatAmount(data.net_amount ?? 0)}`;
  };

  const renderTable = () => {
    if (!salesData) return null;
    const products = salesData;
    if (timeRange === "month") {
      // Generate array of months between startDate and endDate (inclusive)
      const monthList = [];
      let current = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
      const end = new Date(endDate.getFullYear(), endDate.getMonth(), 1);
      while (current <= end) {
        monthList.push({
          key: `${current.getFullYear()}-${current.getMonth() + 1}`,
          label: current.toLocaleString("default", { month: "short" }),
          year: current.getFullYear(),
          month: current.getMonth(),
        });
        current = new Date(current.getFullYear(), current.getMonth() + 1, 1);
      }
      return (
        <div className="overflow-x-auto">
          <table className="table table-zebra w-full">
            <thead>
              <tr>
                <th>State</th>
                {monthList.map((m) => (
                  <th key={m.key}>
                    {m.label} {m.year}
                  </th>
                ))}
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {products.map((product, index) => {
                const isGrandTotal =
                  product.state?.toLowerCase().includes("total") ||
                  product.state?.toLowerCase().includes("grand");
                return (
                  <tr
                    key={index}
                    className={isGrandTotal ? "font-bold" : ""}
                    style={
                      isGrandTotal
                        ? { backgroundColor: "#e0f2fe", color: "#0c4a6e" }
                        : {}
                    }
                  >
                    <td
                      className="whitespace-nowrap capitalize transition-colors duration-150 cursor-pointer hover:bg-blue-100 hover:text-blue-700"
                      style={{ cursor: "pointer" }}
                      onClick={() => {
                        if (product.state && !isGrandTotal) {
                          const params = new URLSearchParams();
                          params.append("state", product.state);
                          params.append(
                            "startDate",
                            startDate.toLocaleDateString("en-CA").split("T")[0]
                          );
                          params.append(
                            "endDate",
                            endDate.toLocaleDateString("en-CA").split("T")[0]
                          );
                          navigate(
                            `/analytics/product-by-state?${params.toString()}`,
                            {
                              state: {
                                fromPage: "/analytics/zone-detail-sales-trend",
                                startDate:
                                  startDate.toLocaleDateString("en-CA"),
                                endDate: endDate.toLocaleDateString("en-CA"),
                                timeRange: timeRange,
                                zone: zoneParam,
                                postingAccount: postingAccount,
                                showQuantity: showQuantity,
                                showAllStates: showAllStates,
                              },
                            }
                          );
                        }
                      }}
                    >
                      {product.state}
                    </td>
                    {monthList.map((m) => {
                      const key = m.label;
                      return <td key={m.key}>{formatCell(product[key])}</td>;
                    })}
                    <td>{formatCell(product.total)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      );
    } else {
      // For daily view
      const dates = Object.keys(products[0]?.daily_data || {})
        .sort((a, b) => new Date(a) - new Date(b))
        .map((date) => {
          const d = new Date(date);
          return {
            fullDate: date,
            displayDate: `${d.getDate()}-${d.toLocaleString("default", {
              month: "short",
            })}`,
          };
        });
      return (
        <div className="overflow-x-auto">
          <table className="table table-zebra w-full">
            <thead>
              <tr>
                <th>State</th>
                {dates.map(({ fullDate, displayDate }) => (
                  <th key={fullDate}>
                    {displayDate} {fullDate.split("-")[0]}
                  </th>
                ))}
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {products.map((product, index) => {
                const isGrandTotal =
                  product.state?.toLowerCase().includes("total") ||
                  product.state?.toLowerCase().includes("grand");
                return (
                  <tr
                    key={index}
                    className={isGrandTotal ? "font-bold" : ""}
                    style={
                      isGrandTotal
                        ? { backgroundColor: "#e0f2fe", color: "#0c4a6e" }
                        : {}
                    }
                  >
                    <td
                      className="whitespace-nowrap capitalize transition-colors duration-150 cursor-pointer hover:bg-blue-100 hover:text-blue-700"
                      style={{ cursor: "pointer" }}
                      onClick={() => {
                        if (product.state && !isGrandTotal) {
                          const params = new URLSearchParams();
                          params.append("state", product.state);
                          params.append(
                            "startDate",
                            startDate.toLocaleDateString("en-CA").split("T")[0]
                          );
                          params.append(
                            "endDate",
                            endDate.toLocaleDateString("en-CA").split("T")[0]
                          );
                          navigate(
                            `/analytics/product-by-state?${params.toString()}`,
                            {
                              state: {
                                fromPage: "/analytics/zone-detail-sales-trend",
                                startDate:
                                  startDate.toLocaleDateString("en-CA"),
                                endDate: endDate.toLocaleDateString("en-CA"),
                                zone: zoneParam,
                                timeRange: timeRange,
                                postingAccount: postingAccount,
                                showQuantity: showQuantity,
                                showAllStates: showAllStates,
                              },
                            }
                          );
                        }
                      }}
                    >
                      {product.state}
                    </td>
                    {dates.map(({ fullDate }) => (
                      <td key={`${product.stat_value}-${fullDate}`}>
                        {formatCell(product.daily_data?.[fullDate])}
                      </td>
                    ))}
                    <td>
                      {formatCell({
                        net_amount: product.total_net_amount,
                        qty: product.total_qty,
                      })}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      );
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-500"></div>
      </div>
    );
  }

  if (error) {
    return <div className="text-red-500 text-center mt-8">{error}</div>;
  }

  return (
    <div className="space-y-6 p-4">
      <button
        className="btn btn-ghost btn-sm mb-2"
        onClick={() => {
          // Check if we came from a specific page via navigation state
          if (location.state?.fromPage) {
            // Navigate back to the specific page with preserved filters
            navigate(location.state.fromPage, {
              state: {
                startDate: location.state.startDate,
                endDate: location.state.endDate,
                timeRange: location.state.timeRange,
                postingAccount: location.state.postingAccount,
                showQuantity: location.state.showQuantity,
                showAllStates: location.state.showAllStates,
              },
            });
          } else {
            // Default fallback - go back in history
            navigate(-1);
          }
        }}
      >
        ← Back
      </button>
      <h1 className="text-2xl font-bold mb-4">
        Zone Detail Sales Trend: {zoneParam}
      </h1>
      <div className="flex flex-wrap gap-4 items-center w-full lg:w-auto mb-4">
        <select
          className="select select-bordered w-full lg:w-40"
          value={postingAccount}
          onChange={(e) => setPostingAccount(e.target.value)}
        >
          <option value="">All</option>
          <option value="amazon">Amazon</option>
          <option value="flipkart">Flipkart</option>
          <option value="shopify">Shopify</option>
          <option value="tata 1mg">Tata 1mg</option>
          <option value="palsons derma pvt ltd">Palsons Derma Pvt Ltd</option>
        </select>
        <div className="flex gap-2 items-center w-full lg:w-auto">
          <DatePicker
            selected={startDate}
            onChange={(date) => {
              if (timeRange === "month") {
                const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
                setStartDate(toLocalMidnight(firstDay));
              } else if (timeRange === "hour") {
                setStartDate(date);
              } else {
                setStartDate(toLocalMidnight(date));
              }
            }}
            className="text-sm input input-bordered w-full lg:w-40"
            dateFormat={
              timeRange === "month" 
                ? "yyyy-MM" 
                : timeRange === "hour" 
                ? "yyyy-MM-dd HH:mm" 
                : "yyyy-MM-dd"
            }
            showMonthYearPicker={timeRange === "month"}
            showFullMonthYearPicker={timeRange === "month"}
            showTimeSelect={timeRange === "hour"}
            timeIntervals={60}
            timeFormat="HH:mm"
            timeCaption="Time"
            minDate={
              timeRange === "month"
                ? new Date(endDate.getFullYear(), endDate.getMonth() - 11, 1)
                : new Date(endDate.getTime() - 365 * 24 * 60 * 60 * 1000)
            }
            maxDate={endDate}
          />
          <DatePicker
            selected={endDate}
            onChange={(date) => {
              if (timeRange === "month") {
                const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0);
                setEndDate(toLocalMidnight(lastDay));
                const newStartDate = new Date(date.getFullYear(), date.getMonth() - 2, 1);
                if (toLocalMidnight(date) < startDate) {
                  setStartDate(toLocalMidnight(newStartDate));
                }
              } else if (timeRange === "hour") {
                setEndDate(date);
                const maxHoursBack = 168;
                const newStartDate = new Date(date);
                newStartDate.setHours(newStartDate.getHours() - maxHoursBack);
                if (date < startDate) {
                  setStartDate(newStartDate);
                }
              } else {
                setEndDate(toLocalMidnight(date));
                if (toLocalMidnight(date) < startDate) {
                  setStartDate(getInitialStartDate(timeRange, date));
                }
              }
            }}
            className="text-sm input input-bordered w-full lg:w-40"
            dateFormat={
              timeRange === "month" 
                ? "yyyy-MM" 
                : timeRange === "hour" 
                ? "yyyy-MM-dd HH:mm" 
                : "yyyy-MM-dd"
            }
            showMonthYearPicker={timeRange === "month"}
            showFullMonthYearPicker={timeRange === "month"}
            showTimeSelect={timeRange === "hour"}
            timeIntervals={60}
            timeFormat="HH:mm"
            timeCaption="Time"
            maxDate={new Date()}
          />
        </div>
        <div className="btn-group">
          <button
            className={`btn btn-sm ${timeRange === "hour" ? "btn-primary" : "btn-ghost"
              }`}
            onClick={() => setTimeRange("hour")}
          >
            Hourly
          </button>
          <button
            className={`btn btn-sm ${timeRange === "day" ? "btn-primary" : "btn-ghost"
              }`}
            onClick={() => setTimeRange("day")}
          >
            Daily
          </button>
          <button
            className={`btn btn-sm ${timeRange === "month" ? "btn-primary" : "btn-ghost"
              }`}
            onClick={() => setTimeRange("month")}
          >
            Monthly
          </button>
        </div>
        <div className="form-control">
          <label className="label cursor-pointer">
            <span className="label-text mr-2">Show Quantity</span>
            <input
              type="checkbox"
              className="toggle toggle-primary"
              checked={showQuantity}
              onChange={(e) => setShowQuantity(e.target.checked)}
            />
          </label>
        </div>
      </div>
      <div className="bg-white rounded-lg shadow">
        <div className="p-6">
          <h3 className="text-lg font-semibold mb-4">
            State Performance Details ({showQuantity ? "Quantity" : "Amount"})
          </h3>
          <div className="overflow-x-auto">{renderTable()}</div>
        </div>
      </div>
    </div>
  );
};

export default ZoneDetailSalesTrend;
